var h = Object.defineProperty;
var u = (s, e, t) => e in s ? h(s, e, { enumerable: !0, configurable: !0, writable: !0, value: t }) : s[e] = t;
var n = (s, e, t) => u(s, typeof e != "symbol" ? e + "" : e, t);
import { E as g } from "./platform-components-assets/index-es.js";
import _ from "./DomHelper.js";
import c from "./Selection.js";
const i = class i {
  /**
   * @param {Object} options The options to be used by the SelectionHelper instance.
   * @param {Node.ELEMENT_NODE} options.contextContainer The context container element to which this instance is bound to.
   * @param {String[]} options.ignoredElements An array of string selectors to ignore when capturing nodes with this helper.
   * @param {Object} options.nodeSelectors A data object to define the node selectors for the highlight data.
   *
   * @example
   * const contextEl = document.querySelector('main');
   *
   * const selectionHelper = new SelectionHelper({
   *   contextContainer: contextEl,
   *   ignoredElements: [
   *     'span',
   *     '.nebula-button',
   *   ],
   * });
   *
   * // update from data
   * selectionHelper.updateFromData(...);
   *
   * // or, listen to events
   * selectionHelper.on('select', ...);
   */
  constructor(e) {
    n(this, "_clientId");
    n(this, "_domHelper");
    n(this, "_eventBus");
    n(this, "_selection");
    this._clientId = i.getNextClientId(), this._domHelper = new _({
      contextContainer: e == null ? void 0 : e.contextContainer,
      ignoredElements: e == null ? void 0 : e.ignoredElements,
      nodeSelectors: e == null ? void 0 : e.nodeSelectors
    }), this._selection = new c({
      contextContainer: this.contextContainer,
      ignoredElements: this.ignoredElements,
      nodeSelectors: this.nodeSelectors
    }), this._eventBus = new g(), i.add(this);
  }
  /**
   * The unique client ID of this instance.
   * @type {Number}
   * @readonly
   *
   * @example
   * const selectionHelper = new SelectionHelper();
   *
   * // prints the client-id
   * console.log(selectionHelper.clientId);
   *
   * // throws an exception
   * selectionHelper.clientId = 123;
   */
  get clientId() {
    return this._clientId;
  }
  get contextContainer() {
    return this.domHelper.contextContainer;
  }
  /**
   * The context-container to which this instance is bound to.
   * @type {Node.ELEMENT_NODE}
   */
  set contextContainer(e) {
    this.domHelper.contextContainer = e;
  }
  /**
   * The immediate parent of context-container to which this instance is bound to.
   * @type {Node.ELEMENT_NODE}
   * @readonly
   */
  get contextContainerParent() {
    var e;
    return ((e = this.contextContainer) == null ? void 0 : e.parentNode) || null;
  }
  /**
   * The {@link DomHelper} instance used by this selection.
   * @type {DomHelper}
   * @readonly
   */
  get domHelper() {
    return this._domHelper;
  }
  /**
   * The event bus for this instance.
   * @type {EventBus}
   * @readonly
   */
  get eventBus() {
    return this._eventBus;
  }
  /**
   * The array of ignored element selectors.
   * @type {String[]}
   */
  get ignoredElements() {
    return this.domHelper.ignoredElements;
  }
  set ignoredElements(e) {
    this.domHelper.ignoredElements = e;
  }
  /**
   * The node selectors data object.
   * @type {NodeSelectorsData}
   */
  get nodeSelectors() {
    return this.domHelper.nodeSelectors;
  }
  set nodeSelectors(e) {
    this.domHelper.nodeSelectors = e;
  }
  /**
   * The {@link Selection} instance linked to this helper.
   * @type {Selection}
   * @readonly
   */
  get selection() {
    return this._selection;
  }
  /**
   * Clean up any handlers and registrations for this instance.
   */
  destroy() {
    i.remove(this.clientId);
  }
  /**
   * Dispatch an event on the event-bus.
   * @private
   * @param {String} eventName The name of the event to dispatch.
   * @param {Any} data The data to pass along into the event data.
   */
  _dispatchEvent(e, t) {
    this.eventBus.emit(
      e,
      {
        context: this,
        data: t,
        selection: this.selection
      }
    );
  }
  /**
   * Handler for selection start from the singleton.
   * @private
   * @emits SelectionHelper#selection:start
   */
  _onSelectionStart() {
    this._dispatchEvent("selection:start");
  }
  /**
   * Event handler for 'selection:change' from the singleton.
   * @private
   * @emits SelectionHelper#selection:change
   */
  _onSelectionChange(e) {
    this.updateSelection(e), this._dispatchEvent("selection:change");
  }
  /**
   * Handler for selection stop from the singleton.
   * @private
   * @emits SelectionHelper#selection:stop
   */
  _onSelectionStop(e) {
    this.updateSelection(e), this._dispatchEvent("selection:stop");
  }
  /**
   * Updates the internal selection object using an external selection object.
   * @param {Selection} selection An instance of `Selection`.
   */
  updateSelection(e) {
    const t = e.getData();
    if (!(!t.startNode || !t.endNode)) {
      if (!this.domHelper.isNodeInContextContainer(t.startNode) || !this.domHelper.isNodeInContextContainer(t.endNode)) {
        this.selection.setStale();
        return;
      }
      this.selection.updateFromData(
        t
      ), this.selection.setFresh();
    }
  }
  static get isLoaded() {
    return this._isLoaded;
  }
  static get selection() {
    return this._selection;
  }
  /**
   * Registers the SelectionHelper instance with the singleton for managing it better.
   * This also helps ensure we only attach event-listeners to the DOM only when there
   * are active instances on the page.
   * @param {SelectionHelper} selectionHelper An instance of the SelectionHelper.
   */
  static add(e) {
    this.selectionHelpers.set(
      e.clientId,
      e
    ), this.load(), e.updateSelection(
      this.selection
    );
  }
  /**
   * Adds event-listeners to the document.
   * @private
   */
  static _addEventListeners() {
    this._eventAbortController = new AbortController();
    const { signal: e } = this._eventAbortController;
    document.addEventListener(
      "pointerdown",
      (t) => this._onPointerDown(t),
      {
        signal: e
      }
    ), document.addEventListener(
      "pointerup",
      (t) => this._onPointerUp(t),
      {
        signal: e
      }
    ), document.addEventListener(
      "selectionchange",
      (t) => this._onSelectionChange(t),
      {
        signal: e
      }
    );
  }
  /**
   * Helper method to clear all text selections from the page.
   */
  static clearSelection() {
    document.getSelection().empty();
  }
  /**
   * Returns a unique numeric client-id for the current window and increments the nextClientId counter by 1.
   * @returns {Number} A unique numeric client-id for the current window.
   */
  static getNextClientId() {
    const e = this._nextClientId;
    return this._nextClientId += 1, e;
  }
  /**
   * This takes in a selection with either single or multiple selection ranges
   * and returns one normalized range across the selection.
   * This is used in cases where some web-browsers like Firefox may return
   * non-contiguous selection ranges causing the selection/highlight to have
   * incorrect offsets.
   * @param {window.Selection} selection A native `window.Selection` object.
   * @returns {?Range} Returns the normalized range object for the given selection, `null` otherwise.
   */
  static getNormalizedSelectionRange(e = null) {
    const t = e || window.getSelection(), { rangeCount: r } = t;
    if (r < 1)
      return null;
    const l = t.getRangeAt(0), a = t.getRangeAt(r - 1), o = new Range();
    return o.setStart(
      l.startContainer,
      l.startOffset
    ), o.setEnd(
      a.endContainer,
      a.endOffset
    ), o;
  }
  /**
   * Loads and initializes the SelectionHelper singleton. This method can be safely
   * called multiple times as the internal checks ensure that the singleton initializes
   * just once.
   */
  static load() {
    this.isLoaded || (this._selection = new c(), this.updateSelection(), this._addEventListeners(), this._isLoaded = !0);
  }
  /**
   * Event handler for the `pointerdown` event.
   * @private
   */
  static _onPointerDown() {
    this._isCapturingSelection = !0, this._onSelectionStart();
  }
  /**
   * Event handler for the `pointerup` event.
   * @private
   */
  static _onPointerUp() {
    this._isCapturingSelection = !1, this.updateSelection(), this._onSelectionStop();
  }
  /**
   * Event handler for the `selectionchange` event.
   * @private
   */
  static _onSelectionChange() {
    this._isCapturingSelection && this.updateSelection();
  }
  /**
   * Handler for selection start.
   * @private
   */
  static _onSelectionStart() {
    this.selectionHelpers.forEach((e) => {
      e._onSelectionStart(this.selection);
    });
  }
  /**
   * Handler for selection start.
   * @private
   */
  static _onSelectionStop() {
    this.selectionHelpers.forEach((e) => {
      e._onSelectionStop(this.selection);
    });
  }
  /**
   * De-registers the SelectionHelper instance with the singleton for managing it better.
   * This helps ensure we clean up any event-listeners attached to the DOM after the last
   * instance has been removed from use on the page.
   * @param {SelectionHelper} selectionHelper An instance of the SelectionHelper.
   */
  static remove(e) {
    this.selectionHelpers.has(e) && (this.selectionHelpers.delete(e), this.selectionHelpers.size === 0 && this.unload());
  }
  /**
   * Removes event listeners from the document
   * @private
   */
  static _removeEventListeners() {
    this._eventAbortController.abort();
  }
  /**
   * Unloads the SelectionHelper singleton. This method can be safely called multiple times
   * as the internal checks ensure that the singleton unloads just once.
   */
  static unload() {
    this.isLoaded && (this._removeEventListeners(), this._selection = null, this._isLoaded = !1);
  }
  /**
   * Updates the selection data based on the current
   * selection in the window.
   */
  static updateSelection() {
    const e = this.getNormalizedSelectionRange();
    if (!e) {
      this.selection.clearData();
      return;
    }
    this.selection.updateFromRange(e), this.selectionHelpers.forEach((t) => {
      t._onSelectionChange(this.selection);
    });
  }
};
// +===================================+
// | Static implementation starts here |
// +===================================+
n(i, "_eventAbortController"), n(i, "_isCapturingSelection", !1), n(i, "_isLoaded", !1), n(i, "_nextClientId", 1), n(i, "_selection", null), n(i, "selectionHelpers", /* @__PURE__ */ new Map());
let d = i;
export {
  d as default
};
